import React, { useEffect, useState } from 'react';
import { Table, Badge, Spinner, Alert, Button } from 'react-bootstrap';
import { formatCurrency } from '../../utility';
import apiService from '../../services/apiServices';
import moment from 'moment';
import BrandTable from '../core/components/BrandTable/BrandTable';
import { FaBoxOpen, FaShoppingCart, FaUser, FaHistory, FaArrowRight } from 'react-icons/fa';

const StockAllItems = ({ itemId, variantId, availableItems = [] }) => {
    const [loading, setLoading] = useState(false);
    const [soldItems, setSoldItems] = useState([]);
    const [totalSold, setTotalSold] = useState(0);
    const [page, setPage] = useState(1);
    const [error, setError] = useState(null);
    const limit = 20;

    useEffect(() => {
        fetchSoldItems();
    }, [itemId, variantId, page]);

    const fetchSoldItems = async () => {
        try {
            setLoading(true);
            const res = await apiService.get(`/stock/items/${itemId}/sales-history`, {
                params: { variantId, page, limit }
            });
            setSoldItems(res.data.sales || []);
            setTotalSold(res.data.totalCount || 0);
        } catch (err) {
            console.error("Failed to load sold items", err);
            setError("Could not load sales history.");
        } finally {
            setLoading(false);
        }
    };

    // Prepare unified data
    // Page 1 shows Available items at the top
    const displayData = page === 1
        ? [
            ...availableItems.filter(b => b.availableQty > 0 || b.status === 'Available').map(b => ({
                ...b,
                type: 'available',
                id: `avail-${b.batch_number || b._id}`,
                qty: b.availableQty || 1,
                date: b.purchaseDate || b.createdAt,
                ref: b.purchase?.referenceNumber || b.batch_number || 'Stock'
            })),
            ...soldItems.map(s => ({
                ...s,
                type: 'sold',
                id: `sold-${s.invoice_id}-${s._id}`,
                qty: s.quantity,
                date: s.invoice_date,
                ref: s.invoice_id
            }))
        ]
        : soldItems.map(s => ({
            ...s,
            type: 'sold',
            id: `sold-${s.invoice_id}-${s._id}`,
            qty: s.quantity,
            date: s.invoice_date,
            ref: s.invoice_id
        }));

    const columns = [
        {
            key: 'type',
            label: 'Status',
            render: (val) => (
                <Badge bg={val === 'available' ? 'success' : 'secondary'} className="px-2 py-1">
                    {val === 'available' ? 'Available' : 'Sold'}
                </Badge>
            )
        },
        {
            key: 'ref',
            label: 'Reference / Invoice',
            render: (val, row) => (
                <div className="fw-bold">
                    {row.type === 'sold' ? <FaShoppingCart className="me-2 text-muted" size={12} /> : <FaBoxOpen className="me-2 text-muted" size={12} />}
                    {val}
                </div>
            )
        },
        {
            key: 'date',
            label: 'Date',
            render: (val) => moment(val).format('LLL')
        },
        {
            key: 'qty',
            label: 'Qty',
            align: 'center',
            render: (val) => <span className="fw-bold">{val}</span>
        },
        {
            key: 'price',
            label: 'Rate',
            align: 'right',
            render: (val, row) => formatCurrency(row.type === 'sold' ? val : row.sellingPrice)
        },
        {
            key: 'info',
            label: 'Details',
            render: (_, row) => (
                <div className="small">
                    {row.type === 'sold' ? (
                        <div className="text-muted">
                            <FaUser size={10} className="me-1" /> {row.customer_name || 'Walk-in'}
                        </div>
                    ) : (
                        <div className="text-muted">
                            Batch: {row.batch_number || 'N/A'}
                        </div>
                    )}
                </div>
            )
        }
    ];

    if (error) return <Alert variant="danger" className="m-3">{error}</Alert>;

    return (
        <div className="p-3">
            <BrandTable
                loading={loading && displayData.length === 0}
                data={displayData}
                columns={columns}
                emptyMessage="No items found."
                striped
                hover
            />
            {totalSold > limit && (
                <div className="d-flex justify-content-between align-items-center mt-3 px-3">
                    <div className="small text-muted">Showing {displayData.length} records</div>
                    <div className="d-flex gap-2">
                        <Button size="sm" variant="outline-primary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                        <Button size="sm" variant="primary" disabled={page * limit >= totalSold} onClick={() => setPage(p => p + 1)}>Next</Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StockAllItems;
